import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { subscriptionAPI, paymentAPI } from "../api/subscriptionAPI";
import { toast } from "react-toastify";

// Subscription Queries Hook
export const useSubscriptions = (params = {}) => {
  return useQuery({
    queryKey: ["subscriptions", params],
    queryFn: () => subscriptionAPI.getAllSubscriptions(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to fetch subscriptions");
    }
  });
};

// Single Subscription Query Hook
export const useSubscription = (id) => {
  return useQuery({
    queryKey: ["subscription", id],
    queryFn: () => subscriptionAPI.getSubscriptionById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to fetch subscription");
    }
  });
};

// Subscription Analytics Query Hook
export const useSubscriptionAnalytics = (params = {}) => {
  return useQuery({
    queryKey: ["subscriptionAnalytics", params],
    queryFn: () => subscriptionAPI.getSubscriptionAnalytics(params),
    staleTime: 10 * 60 * 1000, // 10 minutes
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to fetch analytics");
    }
  });
};

// Subscription Transactions Query Hook
export const useSubscriptionTransactions = (subscriptionId, params = {}) => {
  return useQuery({
    queryKey: ["subscriptionTransactions", subscriptionId, params],
    queryFn: () => subscriptionAPI.getSubscriptionTransactions(subscriptionId, params),
    enabled: !!subscriptionId,
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to fetch transactions");
    }
  });
};

// Payments/Transactions Query Hook
export const usePayments = (params = {}) => {
  return useQuery({
    queryKey: ["payments", params],
    queryFn: () => paymentAPI.getAllPayments(params),
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to fetch payments");
    }
  });
};

// User Subscriptions Query Hook
export const useUserSubscriptions = (userId, params = {}) => {
  return useQuery({
    queryKey: ["userSubscriptions", userId, params],
    queryFn: () => subscriptionAPI.getUserSubscriptions(userId, params),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to fetch user subscriptions");
    }
  });
};

// Create Subscription Mutation Hook
export const useCreateSubscription = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (subscriptionData) => subscriptionAPI.createSubscription(subscriptionData),
    onSuccess: () => {
      queryClient.invalidateQueries(["subscriptions"]);
      queryClient.invalidateQueries(["subscriptionAnalytics"]);
      toast.success("Subscription created successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create subscription");
    }
  });
};

// Update Subscription Status Mutation Hook
export const useUpdateSubscriptionStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, status, reason, metadata }) => 
      subscriptionAPI.updateSubscriptionStatus(id, status, reason, metadata),
    onSuccess: () => {
      queryClient.invalidateQueries(["subscriptions"]);
      queryClient.invalidateQueries(["subscription"]);
      queryClient.invalidateQueries(["subscriptionAnalytics"]);
      toast.success("Subscription status updated successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update subscription status");
    }
  });
};

// Process Renewal Mutation Hook
export const useProcessRenewal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => subscriptionAPI.processRenewal(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["subscriptions"]);
      queryClient.invalidateQueries(["subscription"]);
      queryClient.invalidateQueries(["subscriptionTransactions"]);
      toast.success("Subscription renewed successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to process renewal");
    }
  });
};

// Create Payment Mutation Hook
export const useCreatePayment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (paymentData) => paymentAPI.createPayment(paymentData),
    onSuccess: () => {
      queryClient.invalidateQueries(["payments"]);
      queryClient.invalidateQueries(["subscriptionTransactions"]);
      toast.success("Payment recorded successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to record payment");
    }
  });
};

// Chart Data Hooks
export const useSubscriptionGrowth = (params = {}) => {
  return useQuery({
    queryKey: ['subscriptionGrowth', params],
    queryFn: () => subscriptionAPI.getSubscriptionGrowth(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to fetch subscription growth data");
    }
  });
};

export const useTopProductsBySubscriptions = (limit = 5) => {
  return useQuery({
    queryKey: ['topProductsBySubscriptions', limit],
    queryFn: () => subscriptionAPI.getTopProductsBySubscriptions(limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to fetch top products data");
    }
  });
};

export const useStatusDistribution = () => {
  return useQuery({
    queryKey: ['statusDistribution'],
    queryFn: () => subscriptionAPI.getStatusDistribution(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to fetch status distribution");
    }
  });
};

export const useRevenueTrends = (params = {}) => {
  return useQuery({
    queryKey: ['revenueTrends', params],
    queryFn: () => subscriptionAPI.getRevenueTrends(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to fetch revenue trends");
    }
  });
};